import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError, resolveAssetUrl } from "@/lib/api";
import { formatPhone, formatHexColor } from "@/lib/format";
import { ToolPageShell, inputCls, Field } from "@/components/AdminTools";
import { Save, Upload, Image as ImageIcon, Eye, Sparkles } from "lucide-react";

const EMPTY = {
  display_name: "",
  tagline: "",
  concierge_email: "",
  phone: "",
  address: "",
  contact_name: "",
  primary_color: "#0B1733",
  accent_color: "#1D4ED8",
  logo_url: "",
  website_url: "",
  scheduling_url: "",
};

export default function PortalSettings() {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [assets, setAssets] = useState([]);
  const fileRef = useRef(null);

  const load = async () => {
    try {
      const r = await api.get("/clinic/branding");
      setForm({ ...EMPTY, ...(r.data || {}) });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
    api.get("/marketing-assets")
      .then((r) => setAssets((r.data || []).filter((a) => a.status === "active")))
      .catch(() => {});
  }, []);

  const onLogoSelect = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Logo too large (2 MB max).");
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      const r = await api.post("/clinic/branding/upload-logo", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((f) => ({ ...f, logo_url: r.data?.url || f.logo_url }));
      toast.success("Logo uploaded — saved to your branding profile.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      // Strip empty string fields so PATCH doesn't store them.
      for (const k of Object.keys(payload)) if (payload[k] === "") delete payload[k];
      const r = await api.patch("/clinic/branding", payload);
      setForm({ ...EMPTY, ...(r.data || {}) });
      toast.success("Branding saved. New PDFs will use these values.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const previewSample = async () => {
    if (!assets.length) {
      toast.error("No active marketing assets to preview yet.");
      return;
    }
    // Pick a program-flavored asset for the visual.
    const candidate =
      assets.find((a) => a.template_kind === "landing_page") ||
      assets.find((a) => a.template_kind === "one_pager") ||
      assets[0];
    try {
      const r = await api.get(`/marketing-assets/${candidate.id}/render`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      window.open(url, "_blank", "noopener");
      setTimeout(() => window.URL.revokeObjectURL(url), 30000);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <ToolPageShell
      eyebrow="Clinic · Branding"
      title="Branding & contact info"
      intro="Stamp your clinic's identity onto every co-branded marketing PDF. Updates apply instantly the next time you download a personalized asset."
      actions={
        <button
          data-testid="portal-branding-preview-sample-btn"
          onClick={previewSample}
          disabled={loading}
          className="inline-flex items-center gap-1.5 bg-white border border-slate-200 text-slate-700 rounded-full px-4 py-2 text-sm font-semibold hover:bg-slate-50 disabled:opacity-60"
        >
          <Eye className="w-3.5 h-3.5" /> Preview a branded sample
        </button>
      }
    >
      {loading ? (
        <div className="text-sm text-slate-500">Loading…</div>
      ) : (
        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 rounded-2xl border border-slate-200 bg-white p-6 space-y-5">
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Display name" required hint="Shown in the PDF header band on every asset.">
                <input
                  data-testid="portal-branding-display-name"
                  value={form.display_name || ""}
                  onChange={(e) => setForm({ ...form, display_name: e.target.value })}
                  className={inputCls}
                  placeholder="Aurora Wellness Clinic"
                />
              </Field>
              <Field label="Tagline" hint="Sits under the clinic name in the PDF header.">
                <input
                  data-testid="portal-branding-tagline"
                  value={form.tagline || ""}
                  onChange={(e) => setForm({ ...form, tagline: e.target.value })}
                  className={inputCls}
                  placeholder="Concierge cash-pay programs · Austin, TX"
                />
              </Field>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Concierge email" hint="Stamped on every PDF footer + CTA.">
                <input
                  data-testid="portal-branding-email"
                  type="email"
                  value={form.concierge_email || ""}
                  onChange={(e) => setForm({ ...form, concierge_email: e.target.value })}
                  className={inputCls}
                  placeholder="care@yourclinic.com"
                />
              </Field>
              <Field label="Phone">
                <input
                  data-testid="portal-branding-phone"
                  value={form.phone || ""}
                  onChange={(e) => setForm({ ...form, phone: formatPhone(e.target.value) })}
                  inputMode="tel"
                  className={inputCls}
                  placeholder="(555) 555-5555"
                />
              </Field>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Address (optional)">
                <input
                  data-testid="portal-branding-address"
                  value={form.address || ""}
                  onChange={(e) => setForm({ ...form, address: e.target.value })}
                  className={inputCls}
                  placeholder="123 Main St, Austin TX 78701"
                />
              </Field>
              <Field label="Concierge contact name (optional)">
                <input
                  data-testid="portal-branding-contact-name"
                  value={form.contact_name || ""}
                  onChange={(e) => setForm({ ...form, contact_name: e.target.value })}
                  className={inputCls}
                  placeholder="Dr. Avery Kim"
                />
              </Field>
              <Field label="Website URL" hint="Used in deployed marketing campaigns and CTAs.">
                <input
                  data-testid="portal-branding-website-url"
                  type="url"
                  value={form.website_url || ""}
                  onChange={(e) => setForm({ ...form, website_url: e.target.value })}
                  className={inputCls}
                  placeholder="https://your-clinic.example.com"
                />
              </Field>
              <Field label="Scheduling URL" hint="Where 'Book / Schedule' CTAs link to. Falls back to your website URL if blank.">
                <input
                  data-testid="portal-branding-scheduling-url"
                  type="url"
                  value={form.scheduling_url || ""}
                  onChange={(e) => setForm({ ...form, scheduling_url: e.target.value })}
                  className={inputCls}
                  placeholder="https://your-clinic.example.com/book"
                />
              </Field>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Primary color" hint="Header band + footer (#RRGGBB).">
                <ColorInput
                  testId="portal-branding-primary"
                  value={form.primary_color || ""}
                  onChange={(v) => setForm({ ...form, primary_color: v })}
                />
              </Field>
              <Field label="Accent color" hint="Icon glyph + section accents.">
                <ColorInput
                  testId="portal-branding-accent"
                  value={form.accent_color || ""}
                  onChange={(v) => setForm({ ...form, accent_color: v })}
                />
              </Field>
            </div>

            <Field label="Logo" hint="Square PNG/JPG/WEBP up to 2 MB. Drawn in the PDF header — square logos look best.">
              <div className="flex items-center gap-3">
                {form.logo_url ? (
                  <img
                    src={resolveAssetUrl(form.logo_url)}
                    alt="Logo preview"
                    className="w-14 h-14 rounded-xl object-cover border border-slate-200 bg-white shrink-0"
                  />
                ) : (
                  <span className="w-14 h-14 rounded-xl border border-dashed border-slate-300 bg-slate-50 flex items-center justify-center shrink-0 text-slate-400">
                    <ImageIcon className="w-5 h-5" />
                  </span>
                )}
                <div className="flex items-center gap-2 flex-wrap">
                  <button
                    type="button"
                    onClick={() => fileRef.current?.click()}
                    disabled={uploading}
                    data-testid="portal-branding-logo-upload"
                    className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-900 bg-white border border-slate-200 rounded-full px-3 py-1.5 hover:bg-slate-50 disabled:opacity-60"
                  >
                    <Upload className="w-3 h-3" /> {uploading ? "Uploading…" : form.logo_url ? "Replace logo" : "Upload logo"}
                  </button>
                  {form.logo_url && (
                    <button
                      type="button"
                      onClick={() => setForm({ ...form, logo_url: "" })}
                      className="text-xs text-red-600 hover:text-red-800"
                    >
                      Clear
                    </button>
                  )}
                </div>
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  onChange={onLogoSelect}
                  className="hidden"
                />
              </div>
            </Field>

            <div className="flex items-center gap-2 pt-2">
              <button
                data-testid="portal-branding-save"
                onClick={save}
                disabled={saving}
                className="inline-flex items-center gap-1.5 bg-slate-900 text-white rounded-full px-5 py-2 text-sm font-semibold hover:bg-slate-800 disabled:opacity-60"
              >
                <Save className="w-3.5 h-3.5" /> {saving ? "Saving…" : "Save branding"}
              </button>
              <span className="text-xs text-slate-500">
                Branding stamps onto every co-branded PDF render — no need to re-deploy assets.
              </span>
            </div>
          </div>

          <BrandPreview brand={form} />
        </div>
      )}
    </ToolPageShell>
  );
}

function ColorInput({ value, onChange, testId }) {
  const safe = /^#[0-9A-Fa-f]{6}$/.test(value || "") ? value : "#0B1733";
  return (
    <div className="flex items-center gap-2">
      <input
        type="color"
        value={safe}
        data-testid={`${testId}-picker`}
        onChange={(e) => onChange(e.target.value)}
        className="h-10 w-12 rounded-lg border border-slate-200 cursor-pointer bg-white"
      />
      <input
        type="text"
        data-testid={`${testId}-hex`}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder="#0B1733"
        className={`${inputCls} flex-1 font-mono text-xs`}
      />
    </div>
  );
}

function BrandPreview({ brand }) {
  const primary = /^#[0-9A-Fa-f]{6}$/.test(brand.primary_color || "") ? brand.primary_color : "#0B1733";
  const accent = /^#[0-9A-Fa-f]{6}$/.test(brand.accent_color || "") ? brand.accent_color : "#1D4ED8";
  const display = brand.display_name || "Your Clinic";
  const initials = display
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase()
    .slice(0, 2) || "NH";
  return (
    <div data-testid="portal-branding-preview" className="rounded-2xl border border-slate-200 bg-white p-5 space-y-4 self-start sticky top-20">
      <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-slate-500 inline-flex items-center gap-1.5">
        <Sparkles className="w-3 h-3 text-amber-500" /> Live preview
      </div>
      <div className="rounded-xl overflow-hidden border border-slate-200">
        <div style={{ backgroundColor: primary }} className="px-4 py-3 flex items-center gap-3">
          {brand.logo_url ? (
            <img
              src={resolveAssetUrl(brand.logo_url)}
              alt="logo"
              className="w-9 h-9 rounded-lg object-cover bg-white shrink-0"
            />
          ) : (
            <div
              style={{ backgroundColor: accent }}
              className="w-9 h-9 rounded-full flex items-center justify-center text-white text-xs font-bold shrink-0"
            >
              {initials}
            </div>
          )}
          <div className="min-w-0 flex-1">
            <div className="text-white font-semibold text-sm truncate">{display}</div>
            <div className="text-[10px] text-slate-300 truncate">{brand.tagline || "Tagline goes here"}</div>
          </div>
          <div className="text-right">
            <div className="text-[8px] text-slate-300 tracking-widest">POWERED BY</div>
            <div className="text-white text-[10px] font-bold">NODERA HEALTH</div>
          </div>
        </div>
        <div className="p-4 space-y-3">
          <div
            style={{ backgroundColor: `${accent}1a`, borderColor: `${accent}55` }}
            className="rounded-lg border px-3 py-2 flex items-center gap-2"
          >
            <span style={{ backgroundColor: accent }} className="w-3 h-3 rounded-full" />
            <span className="text-[11px] font-semibold" style={{ color: primary }}>
              Accent strip
            </span>
          </div>
          <div className="text-[11px] text-slate-600 leading-relaxed">
            Your branded PDFs use this header on every page, plus the accent color for icons and section labels.
          </div>
          <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2">
            <div className="text-[10px] uppercase tracking-widest font-bold text-amber-700">Concierge CTA</div>
            <div className="text-[11px] font-semibold text-slate-900 truncate">
              {brand.concierge_email || "care@yourclinic.com"}
              {brand.phone ? `  ·  ${brand.phone}` : ""}
            </div>
          </div>
        </div>
        <div style={{ backgroundColor: primary }} className="px-4 py-2 text-[9px] text-slate-300 truncate">
          {display} · operated as a licensed practice. Nodera Health is the operational software layer.
        </div>
      </div>
    </div>
  );
}
